/**
 * FlashMessageManager.js
 * -------------------------------
 * Location: src/scripts/contact/core/FlashMessageManager.js
 *
 * Standalone flash message management system for displaying user notifications.
 * Handles display, timing, positioning, and cleanup of flash messages.
 * Shared across the application - not contact-specific.
 */
import { SafeDOM } from '../utils/SafeDOM.js';
import { DEFAULT_FLASH_OPTIONS, FLASH_ICONS, CONSTANTS } from './ContactModalConstants.js';

// Alias for clarity
const FLASH_CONSTANTS = CONSTANTS;

/**
 * Manages flash message display and lifecycle
 */
class FlashMessageManager {
  constructor(options = {}, errorHandler = null, eventManager = null) {
    this.options = { ...DEFAULT_FLASH_OPTIONS, ...options };
    this.errorHandler = errorHandler;
    this.eventManager = eventManager; // Now properly assigned
    this.activeMessages = new Map();
  }

  /**
   * Display a flash message
   */
  show(message, type = 'success', duration = null) {
    if (!SafeDOM.isBrowser) return null;

    if (duration === null) {
      duration = type === 'error' ? this.options.errorDuration : this.options.duration;
    }

    let flashContainer = this.getOrCreateContainer();
    
    if (!this.options.stackMessages) {
      this.clearExistingMessage();
    }

    const flashMessage = this.createFlashMessage(message, type);
    if (!flashMessage) return null;

    this.setupCloseButton(flashMessage);
    SafeDOM.safeAppendChild(flashContainer, flashMessage);
    
    // Show animation
    setTimeout(() => {
      SafeDOM.safeAddClass(flashMessage, FLASH_CONSTANTS.CLASSES.FLASH_SHOW);
    }, 10);

    // Auto-hide
    if (duration > 0) {
      setTimeout(() => {
        this.hide(flashMessage);
      }, duration);
    }

    this.logSuccess('Flash message displayed', { 
      type, 
      message: message.substring(0, 100),
      duration 
    });
    
    return flashMessage;
  }

  /**
   * Hide a specific flash message
   */
  hide(flashMessage) {
    if (!flashMessage || !SafeDOM.isBrowser) return;

    SafeDOM.safeAddClass(flashMessage, FLASH_CONSTANTS.CLASSES.FLASH_HIDE);

    setTimeout(() => {
      SafeDOM.safeRemoveElement(flashMessage);
      this.cleanupContainer();
    }, FLASH_CONSTANTS.TIMING.FLASH_HIDE_DELAY);
  }

  /**
   * Clear all flash messages
   */
  clearAll() {
    const flashMessages = SafeDOM.safeQuerySelectorAll(FLASH_CONSTANTS.SELECTORS.FLASH_MESSAGE);
    flashMessages.forEach(msg => this.hide(msg));
    this.activeMessages.clear();
  }

  /**
   * Show success message
   */
  showSuccess(message, duration = null) {
    return this.show(message, 'success', duration);
  }

  /**
   * Show error message
   */
  showError(message, duration = null) {
    return this.show(message, 'error', duration);
  }

  /**
   * Show warning message
   */
  showWarning(message, duration = null) {
    return this.show(message, 'warning', duration);
  }

  /**
   * Show info message
   */
  showInfo(message, duration = null) {
    return this.show(message, 'info', duration);
  }

  /**
   * Update flash options
   */
  updateOptions(newOptions) {
    Object.assign(this.options, newOptions);
  }

  /**
   * Get or create the flash container
   */
  getOrCreateContainer() {
    let container = SafeDOM.safeQuerySelector(FLASH_CONSTANTS.SELECTORS.FLASH_CONTAINER);
    if (!container) {
      container = this.createContainer();
    }
    return container;
  }

  /**
   * Create flash message container
   */
  createContainer() {
    const container = SafeDOM.safeCreateElement('div', {
      className: `flash-messages-container flash-messages-${this.options.position}`,
      'aria-live': 'polite',
      'aria-atomic': 'false'
    });

    if (container) {
      SafeDOM.safeAppendChild(document.body, container);
    }

    return container;
  }

  /**
   * Create individual flash message element
   */
  createFlashMessage(message, type) {
    const flashId = `flash-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const flashMessage = SafeDOM.safeCreateElement('div', {
      className: `${FLASH_CONSTANTS.CLASSES.FLASH_MESSAGE} flash-message-${type}`,
      id: flashId,
      innerHTML: this.buildMessageHTML(message, type),
      'data-flash-type': type,
      'data-flash-id': flashId
    });

    if (flashMessage) {
      this.activeMessages.set(flashId, {
        element: flashMessage,
        type,
        message,
        timestamp: Date.now()
      });
    }

    return flashMessage;
  }

  /**
   * Build HTML content for flash message
   */
  buildMessageHTML(message, type) {
    const icon = this.options.showIcon ? this.getIcon(type) : '';
    const closeButton = this.options.showCloseButton ? 
      '<button class="flash-close" aria-label="Close message" type="button">&times;</button>' : '';

    return `
      <div class="flash-content">
        ${icon ? `<span class="flash-icon" aria-hidden="true">${icon}</span>` : ''}
        <span class="flash-text">${this.escapeHTML(message)}</span>
        ${closeButton}
      </div>
    `;
  }

  /**
   * Setup close button functionality with improved error handling
   */
  setupCloseButton(flashMessage) {
    if (!this.options.showCloseButton) return;

    const closeButton = flashMessage.querySelector('.flash-close');
    if (closeButton) {
      if (this.eventManager && typeof this.eventManager.add === 'function') {
        // Use the event manager if available
        this.eventManager.add(closeButton, 'click', () => {
          this.hide(flashMessage);
        });
      } else {
        // Fallback to direct event listener
        closeButton.addEventListener('click', () => {
          this.hide(flashMessage);
        });
        
        // Log warning for debugging
        if (this.errorHandler?.logError) {
          this.errorHandler.logError('FlashMessageManager: eventManager not available, using fallback', {
            category: 'flash-message',
            action: 'fallback-event-listener'
          });
        }
      }
    }
  }

  /**
   * Clear existing message if stacking is disabled
   */
  clearExistingMessage() {
    const existingFlash = SafeDOM.safeQuerySelector(FLASH_CONSTANTS.SELECTORS.FLASH_MESSAGE);
    if (existingFlash) {
      this.hide(existingFlash);
    }
  }

  /**
   * Clean up container if empty
   */
  cleanupContainer() {
    const container = SafeDOM.safeQuerySelector(FLASH_CONSTANTS.SELECTORS.FLASH_CONTAINER);
    if (container && container.children.length === 0) {
      SafeDOM.safeRemoveElement(container);
    }
  }

  /**
   * Get icon for message type
   */
  getIcon(type) {
    return FLASH_ICONS[type] || FLASH_ICONS.info;
  }

  /**
   * Escape HTML in message text
   */
  escapeHTML(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Log success message
   */
  logSuccess(message, data) {
    if (this.errorHandler?.logSuccess) {
      this.errorHandler.logSuccess(message, {
        category: 'flash-message',
        action: 'show',
        ...data
      });
    }
  }

  /**
   * Get statistics about active messages
   */
  getStats() {
    return {
      activeCount: this.activeMessages.size,
      messageTypes: Array.from(this.activeMessages.values()).map(msg => msg.type),
      oldestMessage: this.getOldestMessage()
    };
  }

  /**
   * Get oldest active message timestamp
   */
  getOldestMessage() {
    const messages = Array.from(this.activeMessages.values());
    if (messages.length === 0) return null;
    
    return Math.min(...messages.map(msg => msg.timestamp));
  }

  /**
   * Remove messages older than specified time
   */
  cleanupOldMessages(maxAge = 30000) { // 30 seconds default
    const cutoff = Date.now() - maxAge;
    
    for (const [id, data] of this.activeMessages.entries()) {
      if (data.timestamp < cutoff) {
        this.hide(data.element);
        this.activeMessages.delete(id);
      }
    }
  }

  /**
   * Destroy the flash message manager
   */
  destroy() {
    this.clearAll();
    this.activeMessages.clear();

    // Clean up container
    const container = SafeDOM.safeQuerySelector(FLASH_CONSTANTS.SELECTORS.FLASH_CONTAINER);
    if (container) {
      SafeDOM.safeRemoveElement(container);
    }
  }
}

/**
 * Factory function to create a flash message manager
 */
function createFlashMessageManager(options = {}, errorHandler = null, eventManager = null) {
  return new FlashMessageManager(options, errorHandler, eventManager);
}

export { FlashMessageManager, createFlashMessageManager };